import { useForm } from "react-hook-form";
import { classes } from "../../../../../util/style-utils";
import AccountTypeOptions from "./AccountTypeOptions";
import { yupResolver } from "@hookform/resolvers/yup";
import RadioButtonGroup from "../../../../gds/RadioButtonGroup";
import GeaoVerticalStepperLayout from "../../shared/Layout/GeaoVerticalStepperLayout";
import PrincipalFlowVerticalStepperElements from "../../../constants/PrincipalFlowVerticalStepperElements";
import useTitle from "../../../../../hooks/useTitle";
import getBankingInformationSchema from "./validation";
import { useContext, useState, useEffect } from "react";
import { GeaoContext } from "../../../GeaoContextProvider";
import { interviewToFormData } from "./mappers";
import { getBankName } from "../../../../../api/billing";
import Input from "../../../../gds/Input";
import Ssn from "../../../../gdk/Ssn";
import Fein from "../../../../gdk/Fein";
import useFormEvents from "./useFormEvents";

export default function PrincipalBankingInformation() {
    useTitle("Banking Information");

    const { agencyInterview, agentInterview, interviewIsCompleted } = useContext(GeaoContext);
    const isSoleProprietor = agencyInterview?.questionAnswers?.SoleProprietor?.answer === "true";
    const [isValidRoutingNumber, setIsValidRoutingNumber] = useState(true);
    const [isBankNameEditable, setIsBankNameEditable] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        getValues,
        watch,
        setValue,
        control
    } = useForm({
        resolver: yupResolver(getBankingInformationSchema(isSoleProprietor)),
        defaultValues: interviewToFormData(agencyInterview, agentInterview)
    });

    const { onSubmit, onSaveAndExit, saving, hasBankingError } = useFormEvents(getValues, trigger);

    const watchRoutingNumber = watch("routingNumber");

    useEffect(() => {
        if (watchRoutingNumber?.length === 9) {
            getBankName(
                watchRoutingNumber,
                (response) => {
                    setValue("bankName", response.bankName);
                    setIsBankNameEditable(false);
                    setIsValidRoutingNumber(true);
                },
                () => {
                    setIsBankNameEditable(true);
                    setIsValidRoutingNumber(false);
                }
            );
        }
    }, [watchRoutingNumber]);

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={PrincipalFlowVerticalStepperElements}
            currentStepperItem="Banking Information"
            header="Banking Information"
        >
            <form className="form geao-form" onSubmit={handleSubmit(onSubmit)}>
                {
                    hasBankingError && (
                        <gds-alert data-onboarding-control-id="PrincipalFlow:BankingInformation:PrincipalBankingInformation:BankingConfirmationError">
                            <gds-alert-message type="danger">
                                <div className="h4">Banking Information Not Confirmed</div>
                                <p>
                                    We are unable to confirm banking information. Please try again later.
                                    If you continue to experience issues, contact our help desk at
                                    1-800-624-2513 for further assistance.
                                </p>
                            </gds-alert-message>
                        </gds-alert>
                    )
                }
                <div className="flex-row">
                    {isSoleProprietor && (
                        <Ssn trackingId="PrincipalFlow:BankingInformation:PrincipalBankingInformation:TaxpayerSsn"
                            control={control}
                            name="ssn"
                            label={<>
                                Taxpayer Identification Number{/*
                                */}<span className='required'>*</span>
                            </>}
                        />
                    )}

                    {!isSoleProprietor && (
                        <Fein trackingId="PrincipalFlow:BankingInformation:PrincipalBankingInformation:TaxpayerFein"
                            control={control}
                            name="fein"
                            label={<>
                                Taxpayer Identification Number{/*
                                */}<span className='required'>*</span>
                            </>}
                        />
                    )}

                    <div className={classes("form-field", errors.payeeName && "form-field--error")}>
                        <label htmlFor="payee-name" className="text">
                            Name on Bank Account{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input id="payee-name" type="text" placeholder="Name on Bank Account" {...register("payeeName")} />
                        {errors.payeeName &&
                            <span className="form-message">{errors.payeeName.message}</span>
                        }
                    </div>
                </div>

                <RadioButtonGroup trackingId="PrincipalFlow:BankingInformation:PrincipalBankingInformation:AccountType"
                    control={control}
                    name="accountType"
                    options={AccountTypeOptions}
                    legend={<>
                        Account Type{/*
                        */}<span className='required'>*</span>
                    </>}
                    layout="Two Column"
                />

                {!isValidRoutingNumber &&
                    <gds-alert data-onboarding-control-id="PrincipalFlow:BankingInformation:PrincipalBankingInformation:InvalidRoutingNumber">
                        <gds-alert-message type="warning">
                            <div className="h4">Banking Information Not Confirmed</div>
                            <p>
                                We are unable to update your commission banking information due to an invalid routing
                                number. Please verify the routing number and try again.
                            </p>
                        </gds-alert-message>
                    </gds-alert>
                }

                <div className="flex-row label-row">
                    <div className="form-field">
                        <label htmlFor="routing-number" className="text">
                            Routing Number{/*
                            */}<span className='required'>*</span>
                        </label>
                    </div>

                    <div className="form-field">
                        <label htmlFor="bank-name" className="text">
                            Bank Name{/*
                            */}<span className='required'>*</span>
                        </label>
                    </div>
                </div>

                <div className="flex-row">
                    <Input
                        input-id="routing-number"
                        input-value-masking="masked"
                        mask="000000000"
                        full-width
                        {...register("routingNumber")}
                    >
                        {errors.routingNumber &&
                            <span slot="error-message-copy">{errors.routingNumber.message}</span>
                        }
                    </Input>

                    <Input
                        input-id="bank-name"
                        disabled={!isBankNameEditable}
                        full-width
                        {...register("bankName")}
                    >
                        {errors.bankName &&
                            <span slot="error-message-copy">{errors.bankName.message}</span>
                        }
                    </Input>
                </div>

                <div className="flex-row">
                    <div className={classes("form-field", errors.accountNumber && "form-field--error")}>
                        <label htmlFor="account-number" className="text">
                            Account Number{/*
                            */}<span className='required'>*</span>
                        </label>
                        <Input
                            input-id="account-number"
                            input-value-masking="masked"
                            mask="00000000000000000"
                            full-width
                            {...register("accountNumber")}
                        >
                            {errors.accountNumber &&
                                <span slot="error-message-copy">{errors.accountNumber.message}</span>
                            }
                        </Input>
                    </div>
                </div>

                <div className="flex-row">
                    <div className={classes("form-field", errors.reenterAccountNumber && "form-field--error")}>
                        <label htmlFor="re-enter-account-number" className="text">
                            Re-Enter Account Number{/*
                            */}<span className='required'>*</span>
                        </label>
                        <Input
                            input-id="re-enter-account-number"
                            input-value-masking="masked"
                            mask="00000000000000000"
                            full-width
                            {...register("reenterAccountNumber")}
                        >
                            {errors.reenterAccountNumber &&
                                <span slot="error-message-copy">{errors.reenterAccountNumber.message}</span>
                            }
                        </Input>
                    </div>
                </div>

                <div className="flex-row">
                    <p>
                        <span className='required'>*</span> Indicates required field.
                    </p>
                </div>

                <div className="margin-top">
                    <button data-onboarding-button-id="PrincipalFlow:BankingInformation:PrincipalBankingInformation:Continue"
                        type="submit"
                        className="btn btn--primary btn--full-mobile btn--pull-right"
                        disabled={saving || interviewIsCompleted || !isValidRoutingNumber}
                    >
                        <span>Next</span>
                    </button>

                    <button data-onboarding-button-id="PrincipalFlow:BankingInformation:PrincipalBankingInformation:SaveAndExit"
                        type="button"
                        className="btn btn--secondary btn--full-mobile btn--pull-right"
                        onClick={onSaveAndExit}
                        disabled={saving || interviewIsCompleted || !isValidRoutingNumber}
                    >
                        <span>Save And Exit</span>
                    </button>
                </div>
            </form>
        </GeaoVerticalStepperLayout>
    );
}

